import { useState, useCallback, useRef } from 'react';
import { setLabDefaults } from '../data/defaultTestData';
import { uploadFile, deleteFile } from '../components/API';

const STORAGE_KEY = 'customConfig';

function readCached() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

function writeCached(config) {
    try {
        if (config) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(config));
        else sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
}

export function useConfig() {
    const [config, setConfig] = useState(readCached);
    const savedSnapshot = useRef(JSON.stringify(config));

    const hasChanges = useCallback((current) => {
        return JSON.stringify(current) !== savedSnapshot.current;
    }, []);

    const saveConfig = useCallback(async (newConfig) => {
        const blob = new Blob([JSON.stringify(newConfig)], { type: 'application/json' });
        const file = new File([blob], 'config/settings.json', { type: 'application/json' });
        await uploadFile(file);
        if (newConfig.labDefaults) setLabDefaults(newConfig.labDefaults);
        setConfig(newConfig);
        writeCached(newConfig);
        savedSnapshot.current = JSON.stringify(newConfig);
    }, []);

    const resetSection = useCallback(async (sectionKey) => {
        const updated = { ...config };
        delete updated[sectionKey];
        if (!updated.labDefaults && !updated.fxRanges) {
            try { await deleteFile('config/settings.json'); } catch {}
            if (sectionKey === 'labDefaults') setLabDefaults({});
            setConfig(null);
            writeCached(null);
            savedSnapshot.current = JSON.stringify(null);
        } else {
            const blob = new Blob([JSON.stringify(updated)], { type: 'application/json' });
            const file = new File([blob], 'config/settings.json', { type: 'application/json' });
            await uploadFile(file);
            if (sectionKey === 'labDefaults') setLabDefaults({});
            setConfig(updated);
            writeCached(updated);
            savedSnapshot.current = JSON.stringify(updated);
        }
    }, [config]);

    return { config, saveConfig, resetSection, hasChanges };
}
